/*
 * Insights (draft 10, charts like Jobber's Insights page on Oct 29): the business's numbers from its own records, drawn as
 * charts, with Reports beside it for spreadsheets. Overview with the change from the days before, money collected this
 * year next to last year, how clients found us and how they paid, what is owed and coming in, quotes and leads, jobs, and
 * job costing. Then Cancels (InsightsCancels.tsx), Ask the Steward and, for owners, every company below. Nothing here is public.
 */
import { useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  addDays,
  can,
  INACTIVE_REASON_WORDS,
  LEAD_SOURCES,
  toCents,
  type CompanyStatsRow,
  type InsightsAnswer,
  type JobCostRow,
} from "@steward/shared";
import { downloadFile } from "../api/client";
import { useAsk, useAsked, useCompanyStats, useCosts, useForecast, useInsights, useRemoveCost, useSaveCost } from "../api/books";
import { useReport, type CompanySummary } from "../api/hooks";
import { useSession } from "../app/session";
import { BarChart, Change, Donut, Funnel, Legend, LineChart } from "../components/Charts";
import { DataTable } from "../components/DataTable";
import { Combobox, InputText } from "../components/fields";
import { monthName, money, shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, Chip, EmptyState, LoadError, Page, Skeleton, StatusLabel } from "../components/parts";
import { errText } from "../components/work";
import { PlanEnds } from "./PlanEnds";
import { Cancels } from "./InsightsCancels";

const CHANNEL_WORDS: Record<string, string> = {
  google: "Google",
  lsa: "Google Local Services",
  facebook: "Facebook",
  referral: "Referral",
  website: "Website",
  yard_sign: "Yard sign",
  repeat: "Came back",
  jobber: "From Jobber",
  other: "Other",
};
const channelName = (c: string) => CHANNEL_WORDS[c] ?? c;
const METHOD_WORDS: Record<string, string> = {
  card: "Card",
  bank: "Bank",
  klarna: "Klarna",
  cash: "Cash",
  check: "Check",
  crew: "Paid the crew",
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Span = "month" | "three" | "year" | "lastyear";
function spanOf(s: Span, today: string): { from: string; to: string; words: string } {
  const y = Number(today.slice(0, 4));
  if (s === "month") return { from: `${today.slice(0, 7)}-01`, to: today, words: "This month" };
  if (s === "year") return { from: `${String(y)}-01-01`, to: today, words: "This year" };
  if (s === "lastyear") return { from: `${String(y - 1)}-01-01`, to: `${String(y - 1)}-12-31`, words: String(y - 1) };
  const m = Number(today.slice(5, 7));
  const back = new Date(Date.UTC(y, m - 3, 1)).toISOString().slice(0, 10);
  return { from: back, to: today, words: "The last 3 months" };
}

function SpanChips({ span, setSpan, today }: { span: Span; setSpan: (s: Span) => void; today: string }) {
  return (
    <div className="chips" aria-label="Days shown">
      {(["month", "three", "year", "lastyear"] as const).map((v) => (
        <Chip key={v} pressed={span === v} onClick={() => setSpan(v)}>
          {spanOf(v, today).words}
        </Chip>
      ))}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

export function Insights() {
  const { company, role } = useSession();
  const [tab, setTab] = useState<"insights" | "plans" | "reports" | "cancels" | "ask" | "companies">("insights");
  const owner = can(role, "report.children");
  const tabs = [
    ["insights", "Insights"],
    ["plans", "Why plans ended"],
    ["cancels", "Cancels"],
    ["reports", "Reports"],
    ["ask", "Ask the Steward"],
    ...(owner ? ([["companies", "Companies"]] as const) : []),
  ] as const;
  return (
    <Page title="Insights" subtitle={company.name}>
      <div className="segmented page-tabs" role="tablist" aria-label="Insights">
        {tabs.map(([v, label]) => (
          <button key={v} type="button" role="tab" aria-selected={tab === v} onClick={() => setTab(v)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "insights" ? (
        <Numbers />
      ) : tab === "plans" ? (
        <PlanEnds />
      ) : tab === "reports" ? (
        <Reports />
      ) : tab === "cancels" ? (
        <Cancels />
      ) : tab === "ask" ? (
        <Ask />
      ) : (
        <Companies />
      )}
    </Page>
  );
}

function Numbers() {
  const { company } = useSession();
  const today = todayIn(company.timezone);
  const [span, setSpan] = useState<Span>("month");
  const s = spanOf(span, today);
  const q = useInsights(company.id, s.from, s.to);
  const [costsOpen, setCostsOpen] = useState(false);
  const n = q.data;
  return (
    <>
      <SpanChips span={span} setSpan={setSpan} today={today} />
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !n ? (
        <Skeleton rows={8} />
      ) : (
        <div className={q.isPlaceholderData ? "refreshing stack" : "stack"}>
          <Overview n={n} words={s.words} />

          <h2 className="ins-section">Money</h2>
          <Revenue n={n} today={today} />
          <div className="grid-2 ins-two">
            <Card
              title="How they found us"
              action={
                <Button type="secondary" size="small" icon="money" onClick={() => setCostsOpen(true)}>
                  Marketing costs
                </Button>
              }
            >
              <Donut
                parts={n.channels.map((c) => ({ name: channelName(c.channel), value: c.collectedCents }))}
                format={money}
                totalLabel="Collected"
              />
              <ChannelCosts n={n} />
            </Card>
            <Card title="How clients paid">
              {n.payments.failed ? (
                <Banner type="warning">
                  {n.payments.failed} card {n.payments.failed === 1 ? "payment" : "payments"} did not go through in these days.
                </Banner>
              ) : null}
              <Donut
                parts={n.payments.byMethod.map((m) => ({ name: METHOD_WORDS[m.method] ?? m.method, value: m.cents }))}
                format={money}
                totalLabel="Paid"
              />
            </Card>
          </div>

          <h2 className="ins-section">Cash flow</h2>
          <CashFlow n={n} />

          <h2 className="ins-section">Leads and quotes</h2>
          <LeadsAndQuotes n={n} />

          <h2 className="ins-section">Jobs and clients</h2>
          <JobsAndClients n={n} />

          <h2 className="ins-section">Profit</h2>
          <JobCosts rows={n.jobCosts} />
        </div>
      )}
      <CostsModal
        open={costsOpen}
        onClose={() => setCostsOpen(false)}
        year={Number(today.slice(0, 4))}
        month={today.slice(0, 7)}
      />
    </>
  );
}

function Overview({ n, words }: { n: InsightsAnswer; words: string }) {
  const p = n.charts.previous;
  const visits = n.months.reduce((a, m) => a + m.visitsDone, 0);
  const days = `${shortDate(p.from)} to ${shortDate(p.to)}`;
  const k = (now: number, before: number, text: string) => (
    <>
      {text}
      <Change now={now} before={before} />
    </>
  );
  return (
    <Card title="Overview">
      <p className="muted small">
        {words}, next to the same number of days before ({days}).
      </p>
      <div className="ins-kpis">
        <Tile
          label="Collected"
          value={k(n.money.collectedCents, p.collectedCents, money(n.money.collectedCents))}
          hint="Less tips"
        />
        <Tile label="Billed" value={k(n.money.billedCents, p.billedCents, money(n.money.billedCents))} hint="Invoices sent" />
        <Tile label="New leads" value={k(n.charts.leads.created, p.leads, String(n.charts.leads.created))} hint="Came in" />
        <Tile
          label="Quotes won"
          value={k(n.quotes.won, p.quotesWon, String(n.quotes.won))}
          hint={`Of ${String(n.quotes.sent)} sent`}
        />
        <Tile label="New clients" value={k(n.clients.newClients, p.newClients, String(n.clients.newClients))} hint="First bill" />
        <Tile label="Visits done" value={k(visits, p.visitsDone, String(visits))} hint="Finished" />
      </div>
    </Card>
  );
}

function Revenue({ n, today }: { n: InsightsAnswer; today: string }) {
  const y = Number(today.slice(0, 4));
  const by = new Map(n.charts.years.map((m) => [m.month, m.collectedCents]));
  const of = (year: number) => MONTHS.map((_, i) => by.get(`${String(year)}-${String(i + 1).padStart(2, "0")}`) ?? 0);
  const last = of(y - 1);
  const now = of(y);
  const sum = (a: number[]) => a.reduce((x, v) => x + v, 0);
  const sameTimeLastYear = n.charts.years
    .filter((m) => m.month.startsWith(String(y - 1)) && m.month.slice(5) <= today.slice(5, 7))
    .reduce((a, m) => a + m.collectedCents, 0);
  return (
    <Card title="Money collected">
      <div className="ins-big">
        <div>
          <b className="num">{money(sum(now))}</b>
          <Legend items={[{ name: `${String(y)} so far`, tone: "main" }]} />
        </div>
        <div>
          <b className="num">{money(sum(last))}</b>
          <Legend items={[{ name: `All of ${String(y - 1)}`, tone: "soft" }]} />
        </div>
        <div>
          <span className="muted small">Next to the same months last year</span>
          <Change now={sum(now)} before={sameTimeLastYear} />
        </div>
      </div>
      <BarChart
        labels={MONTHS}
        tipLabels={MONTHS.map((m) => m)}
        series={[
          { name: String(y - 1), values: last.map((v) => v / 100), tone: "soft" },
          { name: String(y), values: now.map((v, i) => (i + 1 > Number(today.slice(5, 7)) ? 0 : v / 100)), tone: "main" },
        ]}
        format={(v) => money(Math.round(v * 100))}
        axisPrefix="$"
        height={220}
      />
    </Card>
  );
}

function ChannelCosts({ n }: { n: InsightsAnswer }) {
  const withCost = n.channels.filter((c) => c.costCents > 0);
  if (withCost.length === 0) {
    return <p className="muted small">Type in what each channel cost to see what a new client costs you.</p>;
  }
  return (
    <table className="ins-owing">
      <thead>
        <tr>
          <th>Channel</th>
          <th className="num">Cost</th>
          <th className="num">New clients</th>
          <th className="num">Per new client</th>
        </tr>
      </thead>
      <tbody>
        {withCost.map((c) => (
          <tr key={c.channel}>
            <td>{channelName(c.channel)}</td>
            <td className="num">{money(c.costCents)}</td>
            <td className="num">{c.newClients}</td>
            <td className="num">{c.costPerClientCents === null ? "None yet" : money(c.costPerClientCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CashFlow({ n }: { n: InsightsAnswer }) {
  const { company } = useSession();
  const f = useForecast(company.id);
  const navigate = useNavigate();
  const o = n.charts.owing;
  return (
    <>
      <div className="grid-3 ins-two">
        <Card title="Owed to you">
          <div className="ins-big">
            <div>
              <b className="num">{money(n.money.openCents)}</b>
              <span className="muted small">
                {o.clients === 1 ? "1 client owes you" : `${String(o.clients)} clients owe you`}
              </span>
            </div>
          </div>
          {o.top.length ? (
            <table className="ins-owing">
              <thead>
                <tr>
                  <th>Client</th>
                  <th className="num">Owes</th>
                  <th className="num">Late</th>
                </tr>
              </thead>
              <tbody>
                {o.top.map((c) => (
                  <tr key={c.clientId}>
                    <td>
                      <a
                        href={`/clients/${c.clientId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          void navigate({ to: "/clients/$id", params: { id: c.clientId } });
                        }}
                      >
                        {c.name}
                      </a>
                    </td>
                    <td className="num">{money(c.balanceCents)}</td>
                    <td className={c.lateCents ? "num late" : "num muted"}>{c.lateCents ? money(c.lateCents) : "None"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">Nobody owes you right now.</p>
          )}
        </Card>
        <Card title="Coming in">
          {f.error ? (
            <LoadError error={f.error} retry={() => void f.refetch()} />
          ) : !f.data ? (
            <Skeleton rows={3} />
          ) : (
            <div className="stack">
              {f.data.windows.slice(0, 1).map((w) => (
                <div className="ins-big" key={w.days}>
                  <div>
                    <b className="num">{money(w.bookedCents)}</b>
                    <span className="muted small">Booked in the next {w.days} days</span>
                  </div>
                  <div>
                    <b className="num">{money(w.invoicesCents)}</b>
                    <span className="muted small">Invoices due in {w.days} days</span>
                  </div>
                </div>
              ))}
              {f.data.quotes.openCents ? (
                <p className="muted small">
                  Quotes waiting on an answer come to {money(f.data.quotes.openCents)}
                  {f.data.quotes.likelyCents !== null ? `, about ${money(f.data.quotes.likelyCents)} of it likely.` : "."}
                </p>
              ) : null}
            </div>
          )}
        </Card>
        <Card title="Time to get paid">
          <div className="ins-big">
            <div>
              <b className="num">{n.charts.payDays === null ? "None yet" : `${String(n.charts.payDays)} days`}</b>
              <span className="muted small">From sending an invoice to it being paid</span>
            </div>
          </div>
          {n.money.pastDueCents ? (
            <p>
              <StatusLabel status="critical">{money(n.money.pastDueCents)} past due</StatusLabel>
            </p>
          ) : (
            <p className="muted small">Nothing past due.</p>
          )}
        </Card>
      </div>
      <Forecast />
    </>
  );
}

function Forecast() {
  const { company } = useSession();
  const f = useForecast(company.id);
  if (f.error) return <LoadError error={f.error} retry={() => void f.refetch()} />;
  if (!f.data) return <Skeleton rows={2} />;
  const d = f.data;
  return (
    <Card title="Booked work, next 8 weeks">
      <BarChart
        labels={d.weeks.map((w) => shortDate(w.start))}
        tipLabels={d.weeks.map((w) => `Week of ${shortDate(w.start)}, ${String(w.visits)} visits`)}
        series={[{ name: "Booked work", values: d.weeks.map((w) => w.bookedCents / 100), tone: "main" }]}
        format={(v) => money(Math.round(v * 100))}
        axisPrefix="$"
      />
      <p className="muted small">
        From the schedule and invoices out.{" "}
        {d.payrollHow === "runs"
          ? "Payroll is the weekly average of the last 8 weeks paid."
          : d.payrollHow === "share"
            ? "Payroll is the labor share of the last 90 days of work."
            : "No payroll paid yet, so payroll is not counted."}
      </p>
      <DataTable
        title="Next days"
        rows={d.windows}
        rowKey={(w) => String(w.days)}
        columns={[
          { key: "d", header: "Next", render: (w) => <b>{w.days} days</b> },
          { key: "v", header: "Visits", align: "right", render: (w) => <span className="num">{w.visits}</span> },
          { key: "b", header: "Booked work", align: "right", render: (w) => <span className="num">{money(w.bookedCents)}</span> },
          {
            key: "i",
            header: "Invoices due",
            align: "right",
            render: (w) => <span className="num">{money(w.invoicesCents)}</span>,
          },
          { key: "p", header: "Payroll", align: "right", render: (w) => <span className="num">{money(-w.payrollCents)}</span> },
          { key: "n", header: "Left over", align: "right", render: (w) => <b className="num">{money(w.netCents)}</b> },
        ]}
      />
    </Card>
  );
}

function LeadsAndQuotes({ n }: { n: InsightsAnswer }) {
  const l = n.charts.leads;
  const w = n.charts.quoteWeeks;
  const sentCents = w.reduce((a, x) => a + x.sentCents, 0);
  const wonCents = w.reduce((a, x) => a + x.wonCents, 0);
  return (
    <>
      <div className="grid-2 ins-two">
        <Card title="Lead funnel">
          <Funnel
            steps={[
              { name: "New leads", value: l.created },
              { name: "Got a quote", value: l.quoted },
              { name: "Said yes", value: l.won },
            ]}
          />
        </Card>
        <Card title="Quotes won">
          <div className="ins-big">
            <div>
              <b className="num">{n.quotes.winPercent === null ? "None sent" : `${String(n.quotes.winPercent)}%`}</b>
              <span className="muted small">
                {n.quotes.won} won, {n.quotes.lost} lost, of {n.quotes.sent} sent
              </span>
            </div>
          </div>
          {w.length ? (
            <LineChart
              labels={w.map((x) => shortDate(x.week))}
              values={w.map((x) => (x.sent ? Math.round((x.won / x.sent) * 100) : null))}
              format={(v) => `${String(Math.round(v))}%`}
              max={100}
            />
          ) : (
            <p className="muted">No quotes sent in these days.</p>
          )}
        </Card>
      </div>
      <Card title="Quote value">
        <div className="ins-big">
          <div>
            <b className="num">{money(sentCents)}</b>
            <Legend items={[{ name: `Sent (${String(n.quotes.sent)})`, tone: "soft" }]} />
          </div>
          <div>
            <b className="num">{money(wonCents)}</b>
            <Legend items={[{ name: `Won (${String(n.quotes.won)})`, tone: "main" }]} />
          </div>
        </div>
        {w.length ? (
          <BarChart
            labels={w.map((x) => shortDate(x.week))}
            tipLabels={w.map((x) => `Week of ${shortDate(x.week)}`)}
            series={[
              { name: "Sent", values: w.map((x) => x.sentCents / 100), tone: "soft" },
              { name: "Won", values: w.map((x) => x.wonCents / 100), tone: "main" },
            ]}
            format={(v) => money(Math.round(v * 100))}
            axisPrefix="$"
          />
        ) : null}
      </Card>
    </>
  );
}

function JobsAndClients({ n }: { n: InsightsAnswer }) {
  const rec = n.jobCosts.filter((r) => r.recurring);
  const one = n.jobCosts.filter((r) => !r.recurring && r.jobId !== null);
  const sum = (rows: JobCostRow[], k: "billedCents" | "visits") => rows.reduce((a, r) => a + r[k], 0);
  const avg = (rows: JobCostRow[]) => (sum(rows, "visits") ? Math.round(sum(rows, "billedCents") / sum(rows, "visits")) : null);
  const c = n.clients;
  return (
    <div className="grid-2 ins-two">
      <Card title="Repeating and one-time work">
        <Donut
          parts={[
            { name: "Repeating", value: sum(rec, "billedCents") },
            { name: "One-time", value: sum(one, "billedCents") },
          ]}
          tones={{ Repeating: "c1", "One-time": "c2" }}
          format={money}
          totalLabel="Work done"
        />
        <div className="tiles">
          <Tile
            label="Average repeating visit"
            value={avg(rec) === null ? "None" : money(avg(rec) ?? 0)}
            hint={`${String(sum(rec, "visits"))} visits`}
          />
          <Tile
            label="Average one-time job"
            value={avg(one) === null ? "None" : money(avg(one) ?? 0)}
            hint={`${String(sum(one, "visits"))} visits`}
          />
        </div>
      </Card>
      <Card title="Clients">
        <div className="tiles">
          <Tile label="On a plan" value={String(c.planClients)} hint="Repeating jobs now" />
          <Tile label="Came back" value={String(c.repeatClients)} hint="Billed again" />
          <Tile
            label="Lost"
            value={String(c.lost)}
            hint={c.churnPercent === null ? "None" : `${String(c.churnPercent)}% of clients`}
          />
        </div>
        {c.lostReasons.length ? (
          <Donut
            parts={c.lostReasons.map((r) => ({
              name: (INACTIVE_REASON_WORDS as Record<string, string>)[r.reason] ?? r.reason,
              value: r.count,
            }))}
            format={(v) => String(v)}
            totalLabel="Lost"
          />
        ) : null}
      </Card>
    </div>
  );
}

// Reports ------------------------------------------------------------------------------------------------------------

const REPORT_GROUPS: {
  title: string;
  reports: { key: string; name: string; hint: string; path?: string; dated?: false; action?: "client.bulk" | "payment.record" }[];
}[] = [
  {
    title: "Money",
    reports: [
      { key: "payments", name: "Payments", hint: "Every payment and refund, with the method and tip" },
      {
        key: "invoices-owed",
        name: "Owed and late",
        hint: "Invoices not paid yet, grouped by 30, 60 and 90 days late",
        dated: false,
      },
      {
        key: "invoices",
        name: "All invoices",
        hint: "Every invoice ever, with what is still owed",
        path: "export/invoices.csv",
        dated: false,
        action: "payment.record",
      },
      { key: "job-costs", name: "Job costing", hint: "What each job brought in, its labor and its profit" },
    ],
  },
  {
    title: "Work",
    reports: [
      { key: "visits", name: "Visits", hint: "Every visit on these days, its crew and timer" },
      { key: "cancels", name: "Cancels", hint: "Every cancel and skip, with the reason and how late it was" },
      { key: "jobs", name: "Jobs", hint: "Active jobs and ones made on these days" },
      { key: "quotes", name: "Quotes", hint: "Quotes sent or made, with their total and answer" },
    ],
  },
  {
    title: "Clients",
    reports: [
      { key: "leads", name: "Leads", hint: "Leads that came in, how they found us and their stage" },
      {
        key: "clients",
        name: "All clients",
        hint: "Every client with contact info and lead source",
        path: "export/clients.csv",
        dated: false,
        action: "client.bulk",
      },
    ],
  },
];

function Reports() {
  const { company, can: may } = useSession();
  const today = todayIn(company.timezone);
  const toast = useToast();
  const [span, setSpan] = useState<Span>("year");
  const [busy, setBusy] = useState<string | null>(null);
  const s = spanOf(span, today);
  const get = async (r: (typeof REPORT_GROUPS)[number]["reports"][number]) => {
    const path = r.path ?? `reports/${r.key}?from=${s.from}&to=${s.to}`;
    const file = r.path ? `${r.key}.csv` : r.dated === false ? `${r.key}-${today}.csv` : `${r.key}-${s.from}-to-${s.to}.csv`;
    setBusy(r.key);
    try {
      const ok = await downloadFile(path, file);
      if (!ok) toast("The preview has no server, so there is no file to download.");
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <p className="muted">
        Spreadsheets to download, for your own sheets or your accountant. Profit and loss, the balance sheet and 1099s are in
        Books.
      </p>
      <SpanChips span={span} setSpan={setSpan} today={today} />
      <div className="reports-grid">
        {REPORT_GROUPS.map((g) => (
          <Card key={g.title} title={g.title}>
            <ul className="report-list">
              {g.reports
                .filter((r) => !r.action || may(r.action))
                .map((r) => (
                  <li key={r.key}>
                    <button type="button" onClick={() => void get(r)} disabled={busy !== null}>
                      <div>
                        <b>{r.name}</b>
                        <span>
                          {r.hint}
                          {r.dated === false ? "" : `. ${s.words}.`}
                        </span>
                      </div>
                      <span aria-hidden="true">{busy === r.key ? "Getting it" : "CSV"}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </Card>
        ))}
      </div>
    </>
  );
}

function JobCosts({ rows }: { rows: JobCostRow[] }) {
  const noCrew = rows.reduce((a, r) => a + r.noCrew, 0);
  return (
    <Card title="Job costing">
      <p className="muted">
        For work paid in these days: what it brought in before tips, and what payroll pays for it. Least profitable first.
      </p>
      {noCrew ? (
        <Banner type="warning">{noCrew} paid visits have nobody on them, so they count no labor. Set who worked them.</Banner>
      ) : null}
      <DataTable
        title="Jobs"
        rows={rows}
        rowKey={(r) => r.jobId ?? "by-hand"}
        search={(r) => `${r.name} ${r.clientName}`}
        empty={<EmptyState icon="jobs" title="No paid work in these days yet" />}
        columns={[
          { key: "j", header: "Job", sort: (r) => r.name, render: (r) => <b>{r.name}</b> },
          { key: "c", header: "Client", sort: (r) => r.clientName, render: (r) => r.clientName },
          {
            key: "v",
            header: "Visits",
            align: "right",
            sort: (r) => r.visits,
            render: (r) => <span className="num">{r.visits}</span>,
          },
          {
            key: "b",
            header: "Brought in",
            align: "right",
            sort: (r) => r.billedCents,
            render: (r) => <span className="num">{money(r.billedCents)}</span>,
          },
          {
            key: "l",
            header: "Labor",
            align: "right",
            sort: (r) => r.laborCents,
            render: (r) => <span className="num">{money(r.laborCents)}</span>,
          },
          {
            key: "p",
            header: "Profit",
            align: "right",
            sort: (r) => r.profitCents,
            render: (r) => <span className="num">{money(r.profitCents)}</span>,
          },
          {
            key: "m",
            header: "Margin",
            align: "right",
            sort: (r) => r.marginPercent ?? 0,
            render: (r) =>
              r.marginPercent === null ? (
                ""
              ) : (
                <StatusLabel status={r.marginPercent < 20 ? "critical" : r.marginPercent < 40 ? "warning" : "success"}>
                  {r.marginPercent}%
                </StatusLabel>
              ),
          },
        ]}
      />
    </Card>
  );
}

function CostsModal({ open, onClose, year, month }: { open: boolean; onClose: () => void; year: number; month: string }) {
  const { company } = useSession();
  const costs = useCosts(company.id, year);
  const save = useSaveCost();
  const remove = useRemoveCost();
  const toast = useToast();
  const [m, setM] = useState(month);
  const [channel, setChannel] = useState<string>("google");
  const [amount, setAmount] = useState("");
  const add = async () => {
    const cents = toCents(amount.replace(/[$,\s]/g, "") || "0");
    try {
      await save.mutateAsync({ month: m, channel, amountCents: cents });
      setAmount("");
      toast("Saved");
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work.", { kind: "error" });
    }
  };
  return (
    <Modal title="Marketing costs" open={open} onClose={onClose}>
      <p className="muted">What you spent on each channel in a month. Only typed in here, never taken from an ad platform.</p>
      <div className="form-grid">
        <InputText label="Month" type="month" value={m} onChange={(e) => setM(e.target.value)} />
        <Combobox
          label="How they found us"
          value={channel}
          onChange={setChannel}
          options={LEAD_SOURCES.map((c) => ({ value: c, label: channelName(c) }))}
        />
        <InputText label="Cost" inputMode="decimal" prefix="$" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <Button onClick={() => void add()} loading={save.isPending} disabled={!amount.trim()}>
        Save cost
      </Button>
      <DataTable
        title={`Costs in ${String(year)}`}
        rows={costs.data?.costs ?? []}
        rowKey={(c) => c.id}
        loading={costs.isLoading}
        empty={<EmptyState icon="money" title="No costs typed in yet" />}
        columns={[
          { key: "m", header: "Month", render: (c) => monthName(`${c.month}-01`) },
          { key: "c", header: "Channel", render: (c) => channelName(c.channel) },
          { key: "a", header: "Cost", align: "right", render: (c) => <span className="num">{money(c.amountCents)}</span> },
          {
            key: "x",
            header: "",
            align: "right",
            render: (c) => (
              <Button type="tertiary" size="small" variation="destructive" onClick={() => void remove.mutateAsync(c.id)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
    </Modal>
  );
}

const EXAMPLES = [
  "How much did we collect last month?",
  "Who owes us the most right now?",
  "Which jobs make the least money?",
  "How many new clients came from Google this year?",
];

function Ask() {
  const { company } = useSession();
  const asked = useAsked(company.id);
  const ask = useAsk();
  const [question, setQuestion] = useState("");
  const go = async (text: string) => {
    if (text.trim().length < 3) return;
    try {
      await ask.mutateAsync(text.trim());
      setQuestion("");
    } catch {
      // The error shows under the box.
    }
  };
  return (
    <div className="stack">
      <Card title="Ask the Steward">
        <p className="muted">Ask about your business in plain words. It reads your records only and never changes anything.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void go(question);
          }}
        >
          <InputText label="Your question" value={question} maxLength={500} onChange={(e) => setQuestion(e.target.value)} wide />
          <Button submit loading={ask.isPending} disabled={question.trim().length < 3}>
            Ask
          </Button>
        </form>
        <div className="chips">
          {EXAMPLES.map((x) => (
            <Chip key={x} pressed={false} onClick={() => void go(x)}>
              {x}
            </Chip>
          ))}
        </div>
        {ask.error ? <Banner type="error">{ask.error.message}</Banner> : null}
      </Card>
      {asked.error ? <LoadError error={asked.error} /> : null}
      {(asked.data?.asked ?? []).map((a) => (
        <Card key={a.id}>
          <p>
            <b>{a.question}</b> <span className="muted">{shortDate(a.at.slice(0, 10))}</span>
          </p>
          <p className="ask-answer">{a.answer}</p>
        </Card>
      ))}
    </div>
  );
}

function Companies() {
  const { company, switchCompany } = useSession();
  const today = todayIn(company.timezone);
  const from = addDays(today, -29);
  const report = useReport(company.id);
  const stats = useCompanyStats(company.id, from, today, true);
  const navigate = useNavigate();
  const rows = report.data ?? [];
  const by = new Map((stats.data ?? []).map((s) => [s.companyId, s]));
  const totals = (stats.data ?? []).reduce(
    (a, s) => ({ collected: a.collected + s.collectedCents, clients: a.clients + s.newClients }),
    {
      collected: 0,
      clients: 0,
    },
  );
  const stat = (r: CompanySummary, k: keyof Omit<CompanyStatsRow, "companyId" | "name">) => by.get(r.companyId)?.[k] ?? 0;
  return (
    <>
      <div className="tiles">
        <Tile label="Companies" value={String(rows.length)} hint="Including this one" />
        <Tile label="Collected" value={money(totals.collected)} hint="The last 30 days, all of them" />
        <Tile label="New clients" value={String(totals.clients)} hint="The last 30 days" />
        <Tile label="Clients" value={String(rows.reduce((a, r) => a + r.clients, 0))} hint="Across all of them" />
      </div>
      <DataTable<CompanySummary>
        title="All companies"
        rows={rows}
        rowKey={(r) => r.companyId}
        search={(r) => r.name}
        loading={report.isLoading}
        onRowClick={(r) => {
          switchCompany(r.companyId);
          void navigate({ to: "/home" });
        }}
        columns={[
          { key: "name", header: "Company", sort: (r) => r.name, render: (r) => <b>{r.name}</b> },
          {
            key: "col",
            header: "Collected, 30 days",
            align: "right",
            sort: (r) => stat(r, "collectedCents"),
            render: (r) => <span className="num">{money(stat(r, "collectedCents"))}</span>,
          },
          {
            key: "bil",
            header: "Billed, 30 days",
            align: "right",
            sort: (r) => stat(r, "billedCents"),
            render: (r) => <span className="num">{money(stat(r, "billedCents"))}</span>,
          },
          {
            key: "new",
            header: "New clients",
            align: "right",
            sort: (r) => stat(r, "newClients"),
            render: (r) => <span className="num">{stat(r, "newClients")}</span>,
          },
          {
            key: "clients",
            header: "Clients",
            align: "right",
            sort: (r) => r.clients,
            render: (r) => <span className="num">{r.clients}</span>,
          },
          {
            key: "jobs",
            header: "Active jobs",
            align: "right",
            sort: (r) => r.activeJobs,
            render: (r) => <span className="num">{r.activeJobs}</span>,
          },
        ]}
      />
      <p className="muted">Click a company to work in it.</p>
    </>
  );
}
